import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Button, CollapsableCard, Divider, Text } from '@factorialco/deprecated-design-system'
import { F0Alert, F0AvatarList, F0Button } from '@factorialco/f0-react'
import { SalaryAdvanceEmployeeProfileFragment } from 'generated/resources/graphql'
import groupBy from 'lodash/groupBy'

import Sidepanel from 'modules/core/components/Sidepanel'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { buildUrl } from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

interface EmployeeWithMissingInfo {
  id: number
  firstName: string
  lastName: string
  avatarUrl?: string
  legalEntityId?: number
}

interface EmployeesByLegalEntity {
  legalEntityId: number
  legalEntityName: string
  employees: EmployeeWithMissingInfo[]
}

const MAX_AVATARS_TO_SHOW = 4

interface ResultSectionProps {
  title: string
  employeesByLegalEntity: EmployeesByLegalEntity[]
  linkText: string
  onLinkClick: () => void
}

const ResultSection: React.FC<ResultSectionProps> = ({
  title,
  employeesByLegalEntity,
  linkText,
  onLinkClick,
}) => {
  const [expanded, setExpanded] = React.useState(false)

  const totalEmployees = employeesByLegalEntity.reduce(
    (acc, group) => acc + group.employees.length,
    0
  )

  if (totalEmployees === 0) return null

  return (
    <Box flexDirection="column" gap="s8">
      <CollapsableCard
        Card={
          <Box
            flexDirection="row"
            justifyContent="spaceBetween"
            alignItems="center"
            onClick={() => setExpanded(!expanded)}
            cursor="pointer"
          >
            <Text size="300" weight="semibold" color="grey1000">
              {`${title}: ${totalEmployees}`}
            </Text>
            <Button iconName={expanded ? 'chevron-up' : 'chevron-down'} type="secondary" />
          </Box>
        }
        Content={
          <Box flexDirection="column" gap="s16">
            {employeesByLegalEntity.map((group, index) => {
              const avatars = group.employees.slice(0, MAX_AVATARS_TO_SHOW).map((employee) => ({
                firstName: employee.firstName,
                lastName: employee.lastName,
                src: employee.avatarUrl ? buildUrl(employee.avatarUrl) : undefined,
                type: 'person' as const,
              }))

              const remainingCount = Math.max(0, group.employees.length - MAX_AVATARS_TO_SHOW)

              return (
                <React.Fragment key={group.legalEntityId}>
                  {index > 0 && <Divider />}
                  <Box flexDirection="column" gap="s12" width="full">
                    <Box width="full">
                      <F0AvatarList
                        avatars={avatars}
                        type="person"
                        {...(remainingCount > 0 && { remainingCount })}
                      />
                    </Box>
                    <Box
                      flexDirection="row"
                      alignItems="center"
                      justifyContent="spaceBetween"
                      width="full"
                    >
                      <Text size="200" weight="medium">
                        {group.legalEntityName}
                      </Text>
                      <F0Button onClick={onLinkClick} variant="outline" label={linkText} />
                    </Box>
                  </Box>
                </React.Fragment>
              )
            })}
          </Box>
        }
        open={expanded}
      />
    </Box>
  )
}

const mapToEmployeeWithMissingInfo = (
  employee: SalaryAdvanceEmployeeProfileFragment
): EmployeeWithMissingInfo | null => {
  if (!employee.employeeName) return null

  return {
    id: employee.employeeName.id,
    firstName: employee.employeeName.firstName,
    lastName: employee.employeeName.lastName,
    avatarUrl: employee.employeeName.avatar?.url,
    legalEntityId: employee.legalEntityId ?? undefined,
  }
}

interface Props {
  open: boolean
  onClose: () => void
  selectedEmployees: SalaryAdvanceEmployeeProfileFragment[]
}

export const MissingInfoSidePanel: React.FC<Props> = ({ open, onClose, selectedEmployees }) => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const { company } = useCurrentSession()

  const { employeesWithMissingSalary, employeesWithMissingBank } = useUnsafeMemo(() => {
    const missingSalary: EmployeeWithMissingInfo[] = []
    const missingBank: EmployeeWithMissingInfo[] = []

    selectedEmployees.forEach((employee) => {
      const mappedEmployee = mapToEmployeeWithMissingInfo(employee)
      if (!mappedEmployee) return

      if (employee.missingSalaryInfo) {
        missingSalary.push(mappedEmployee)
      }

      if (employee.missingBankInfo) {
        missingBank.push(mappedEmployee)
      }
    })

    const groupEmployeesByLegalEntity = (
      employees: EmployeeWithMissingInfo[]
    ): EmployeesByLegalEntity[] => {
      const employeesWithLegalEntity = employees.filter(
        (employee) => employee.legalEntityId !== undefined
      )

      const groupedByLegalEntity = groupBy(
        employeesWithLegalEntity,
        (employee) => employee.legalEntityId
      )

      return Object.entries(groupedByLegalEntity).map(([legalEntityIdStr, employeesList]) => {
        const legalEntityId = Number(legalEntityIdStr)
        const legalEntity = company.legalEntities.find((le) => le.id === legalEntityId)
        return {
          legalEntityId,
          legalEntityName: legalEntity?.legalName || `Legal Entity ${legalEntityId}`,
          employees: employeesList,
        }
      })
    }

    return {
      employeesWithMissingSalary: groupEmployeesByLegalEntity(missingSalary),
      employeesWithMissingBank: groupEmployeesByLegalEntity(missingBank),
    }
  }, [selectedEmployees, company.legalEntities])

  const handleNavigateToContractsEdit = () => {
    const employeeIds = employeesWithMissingSalary.flatMap((group) =>
      group.employees.map((emp) => emp.id)
    )

    if (employeeIds.length === 0) return

    navigate(navigation.employees.list.fullPath())
  }

  const handleNavigateToEmployeesEdit = () => {
    const employeeIds = employeesWithMissingBank.flatMap((group) =>
      group.employees.map((emp) => emp.id)
    )
    const selectedLegalEntity = employeesWithMissingBank[0]?.legalEntityId

    navigate(navigation.employees.edit.fullPath(), {
      state: {
        employeesIds: employeeIds,
        selectedLegalEntity,
      },
    })
  }

  return (
    <Sidepanel
      open={open}
      onClose={onClose}
      title={i18n.t('salary_advance.employee.missing_info_side_panel.title')}
      closeLabel={i18n.t('common.close')}
      width="wide"
    >
      <Box gap="s32">
        <Box flexDirection="column" gap="s24">
          <F0Alert
            title={i18n.t('salary_advance.employee.missing_info_side_panel.description.title')}
            description={i18n.t('salary_advance.employee.missing_info_side_panel.description.text')}
            variant="warning"
          />

          <Box flexDirection="column" gap="s16">
            <ResultSection
              title={i18n.t('salary_advance.employee.missing_info_side_panel.missing_salary.title')}
              employeesByLegalEntity={employeesWithMissingSalary}
              linkText={i18n.t(
                'salary_advance.employee.missing_info_side_panel.missing_salary.link_text'
              )}
              onLinkClick={handleNavigateToContractsEdit}
            />

            <ResultSection
              title={i18n.t('salary_advance.employee.missing_info_side_panel.missing_iban.title')}
              employeesByLegalEntity={employeesWithMissingBank}
              linkText={i18n.t(
                'salary_advance.employee.missing_info_side_panel.missing_iban.link_text'
              )}
              onLinkClick={handleNavigateToEmployeesEdit}
            />
          </Box>
        </Box>
      </Box>
    </Sidepanel>
  )
}
